'use client';

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ProPaymentStatus } from '@cfixe-monorepo/shared';
import { consumeRedirectReturn, type RedirectReturn, type RedirectReturnParam } from './redirect-return';

/* Fast polling while the tunnel is blocked, then slower in background so a payment Stripe confirms
   late (network drop, Stripe hiccup) still lands on the success screen. */
const BLOCKING_BUDGET_MS = 30_000;
const BLOCKING_POLL_INTERVAL_MS = 2_000;
const BACKGROUND_BUDGET_MS = 10 * 60_000;
const BACKGROUND_POLL_INTERVAL_MS = 10_000;
const REQUEST_TIMEOUT_MS = 10_000;

/* The payment will never settle: expired pre-reservation (a late Stripe success is refunded by the
   orphan-payment safety net), cancelled or refunded. */
const TERMINAL_UNSETTLED_STATUSES: readonly string[] = [
  ProPaymentStatus.EXPIRED,
  ProPaymentStatus.CANCELLED,
  ProPaymentStatus.REFUNDED,
  ProPaymentStatus.PARTIALLY_REFUNDED,
];

const noop = (): void => {
  /* intentional no-op */
};

/**
 * - `verifying`: blocking check, the tunnel shows only the notice.
 * - `unverified`: blocking budget spent without proof — the tunnel is back, checks go on in background.
 * - `failed` / `pending`: Stripe's own verdict, nothing to poll.
 * - `expired`: the backend says the payment will never settle (e.g. the pre-reservation expired while
 *   the customer was at Klarna) — verification stops, the customer can start over.
 */
export type RedirectReturnPhase = 'verifying' | 'unverified' | 'failed' | 'pending' | 'expired';

export interface RedirectReturnLabels {
  verifying: string;
  unverified: string;
  failed: string;
  pending: string;
  expired: string;
}

/**
 * Answer of `getFinalOutcome`:
 * - `success` / `error`: the tunnel reached a final screen another way (`error` keeps the
 *   `unverified` notice, the payment outcome stays unknown);
 * - `superseded`: the customer moved on to another payment — the old one must no longer drive the
 *   tunnel, verification stops silently.
 */
export type RedirectFinalOutcome = 'success' | 'error' | 'superseded';

export interface RedirectPollResult {
  status: string;
  reservation_uuid?: string;
}

export interface UseRedirectReturnOptions {
  param: RedirectReturnParam;
  /**
   * `false` = leave any return to another tunnel of the page (e.g. an embed widget that did not claim
   * it): the URL is neither read nor cleaned. Default `true`.
   */
  enabled?: boolean;
  poll?: (uuid: string) => Promise<RedirectPollResult>;
  /** Return captured server-side (see `consumeRedirectReturn`). */
  initialStatus?: string;
  initialUuid?: string;
  /** Checked before each poll and before confirming, with the verified payment uuid (see RedirectFinalOutcome). */
  getFinalOutcome?: (uuid: string) => RedirectFinalOutcome | undefined;
  /** Payment proven (PAYED / AUTHORIZED) by the backend. Called at most once. */
  onSettled: (result: { uuid: string; reservationUuid?: string }) => void;
  /** Stripe reported `failed`: the payment was not made. Called at most once (e.g. to release a held slot). */
  onFailed?: (uuid: string) => void;
  /** The backend reports a status that will never settle (see TERMINAL_UNSETTLED_STATUSES). Called at most once. */
  onExpired?: (uuid: string) => void;
}

export interface RedirectReturnState {
  /** pro_payment uuid of the return, once read from the URL. */
  uuid?: string;
  phase?: RedirectReturnPhase;
  /** The tunnel must stay hidden behind the `verifying` notice. */
  isBlocking: boolean;
  /** Same flag, readable in effects of the same commit. */
  isBlockingRef: RefObject<boolean>;
  /**
   * The payment outcome is still being checked (blocking or background). While true the tunnel must
   * neither cancel nor redo the payment: it may already be charged. False once verification ended,
   * whatever the reason — including when it gave up.
   */
  isChecking: boolean;
  /** Drops the notice (e.g. another payment reached the success screen). */
  dismiss: () => void;
}

type Attempt = RedirectReturn & {
  /** First verification start — both budgets derive from it so effect restarts keep them. */
  startedAt?: number;
  /** The blocking phase is over: the tunnel is back, verification continues in background. */
  backgrounded?: boolean;
  /** `onFailed` already ran — effect restarts (StrictMode, new callbacks) must not repeat it. */
  failureHandled?: boolean;
  finished: boolean;
};

export function redirectReturnMessage(phase: RedirectReturnPhase, labels: RedirectReturnLabels): string {
  return labels[phase];
}

/**
 * Resumes a tunnel after a Stripe redirect. A `succeeded` status in the URL is never trusted (the URL
 * is forgeable): the payment is only confirmed once the backend status poll says PAYED / AUTHORIZED.
 */
export function useRedirectReturn({
  param,
  enabled = true,
  poll,
  initialStatus,
  initialUuid,
  getFinalOutcome,
  onSettled,
  onFailed,
  onExpired,
}: UseRedirectReturnOptions): RedirectReturnState {
  const attemptRef = useRef<Attempt | null>(null);
  const [uuid, setUuid] = useState<string | undefined>(undefined);
  const [phase, setPhase] = useState<RedirectReturnPhase | undefined>(undefined);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const isBlockingRef = useRef(false);

  /* Latest callbacks without restarting the verification on every render. Declared before the
     verification effect so they are current when it runs. */
  const onSettledRef = useRef(onSettled);
  const getFinalOutcomeRef = useRef(getFinalOutcome);
  const onFailedRef = useRef(onFailed);
  const onExpiredRef = useRef(onExpired);
  useEffect((): void => {
    onSettledRef.current = onSettled;
    getFinalOutcomeRef.current = getFinalOutcome;
    onFailedRef.current = onFailed;
    onExpiredRef.current = onExpired;
  });

  const dismiss = useCallback((): void => setPhase(undefined), []);

  useEffect((): (() => void) => {
    if (!enabled) return noop;
    if (!attemptRef.current) {
      const found = consumeRedirectReturn(param, { status: initialStatus, uuid: initialUuid });
      if (found) {
        attemptRef.current = { ...found, finished: false };
        setUuid(found.uuid);
      }
    }

    const attempt = attemptRef.current;
    if (!attempt || attempt.finished) return noop;

    if (attempt.status === 'failed' || attempt.status === 'pending') {
      setPhase(attempt.status);
      if (attempt.status === 'failed' && !attempt.failureHandled) {
        attempt.failureHandled = true;
        onFailedRef.current?.(attempt.uuid);
      }
      return noop;
    }
    if (attempt.status !== 'succeeded' || !poll) return noop;

    attempt.startedAt ??= Date.now();
    const blockingDeadline = attempt.startedAt + BLOCKING_BUDGET_MS;
    const giveUpAt = attempt.startedAt + BACKGROUND_BUDGET_MS;
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    let requestTimer: ReturnType<typeof setTimeout> | undefined;

    const setBlocking = (value: boolean): void => {
      isBlockingRef.current = value;
      setIsBlocking(value);
    };
    const cleanup = (): void => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(deadlineTimer);
      clearTimeout(requestTimer);
    };
    /* Every ending releases the tunnel: `cleanup` also cancels the deadline that would have. */
    const stop = (): void => {
      attempt.finished = true;
      cleanup();
      setBlocking(false);
      setIsChecking(false);
    };
    const endOnFinalScreen = (outcome: RedirectFinalOutcome): void => {
      stop();
      setPhase(outcome === 'error' ? 'unverified' : undefined);
    };
    /* Blocking phase over: hand the tunnel back but keep verifying. The notice tells the customer the
       outcome is still unknown. */
    const background = (): void => {
      if (attempt.backgrounded) return;
      attempt.backgrounded = true;
      setBlocking(false);
      setPhase('unverified');
    };
    const pollOnce = (): Promise<RedirectPollResult> =>
      new Promise((resolve, reject): void => {
        /* A request that never settles must not freeze the verification. */
        requestTimer = setTimeout((): void => reject(new Error('timeout')), REQUEST_TIMEOUT_MS);
        poll(attempt.uuid).then(resolve, reject);
      });

    const tick = async (): Promise<void> => {
      const outcome = getFinalOutcomeRef.current?.(attempt.uuid);
      if (outcome) {
        endOnFinalScreen(outcome);
        return;
      }
      if (Date.now() >= giveUpAt) {
        stop();
        return;
      }
      try {
        const res = await pollOnce();
        clearTimeout(requestTimer);
        if (cancelled) return;
        if (res.status === ProPaymentStatus.PAYED || res.status === ProPaymentStatus.AUTHORIZED) {
          const lateOutcome = getFinalOutcomeRef.current?.(attempt.uuid);
          if (lateOutcome) {
            endOnFinalScreen(lateOutcome);
            return;
          }
          stop();
          setPhase(undefined);
          onSettledRef.current({ uuid: attempt.uuid, reservationUuid: res.reservation_uuid });
          return;
        }
        if (TERMINAL_UNSETTLED_STATUSES.includes(res.status)) {
          stop();
          setPhase('expired');
          onExpiredRef.current?.(attempt.uuid);
          return;
        }
      } catch {
        clearTimeout(requestTimer);
        // Network failure or timeout: retry; an error never confirms a payment.
      }
      if (cancelled) return;
      const interval = Date.now() < blockingDeadline ? BLOCKING_POLL_INTERVAL_MS : BACKGROUND_POLL_INTERVAL_MS;
      pollTimer = setTimeout((): void => {
        void tick();
      }, interval);
    };

    setIsChecking(true);
    if (Date.now() >= blockingDeadline) {
      background();
    } else if (!attempt.backgrounded) {
      // Immediate feedback prevents the return from appearing idle while the backend verifies payment.
      setPhase('verifying');
      setBlocking(true);
      /* Independent deadline: even a request that never settles must release the tunnel. */
      deadlineTimer = setTimeout(background, blockingDeadline - Date.now());
    }
    void tick();
    return cleanup;
  }, [param, enabled, poll, initialStatus, initialUuid]);

  return { uuid, phase, isBlocking, isBlockingRef, isChecking, dismiss };
}
